import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff, Check, AlertTriangle } from 'lucide-react';
import { authService } from '../../services/authService';
import { useLanguage } from '../../hooks/useLanguage';

interface ChangePasswordProps {
    userId: string;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({ userId }) => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Refs pour la validation personnalisée
    const currentPasswordRef = useRef<HTMLInputElement>(null);
    const newPasswordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(null);
        // Réinitialiser la validation personnalisée
        e.target.setCustomValidity('');
    };

    const validatePassword = (password: string): boolean => {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        return passwordRegex.test(password);
    };

    // Configuration des messages de validation personnalisés
    const setupCustomValidation = () => {
        const setupInput = (
            ref: React.RefObject<HTMLInputElement>,
            requiredMessage: string,
            patternMessage?: string
        ) => {
            const input = ref.current;
            if (!input) return;

            const handleInvalid = () => {
                if (input.validity.valueMissing) {
                    input.setCustomValidity(requiredMessage);
                } else if (input.validity.patternMismatch && patternMessage) {
                    input.setCustomValidity(patternMessage);
                }
            };

            const handleInput = () => {
                input.setCustomValidity('');
            };

            input.addEventListener('invalid', handleInvalid);
            input.addEventListener('input', handleInput);
        };

        setupInput(currentPasswordRef, t('changePassword.validation.currentPasswordRequired'));
        setupInput(newPasswordRef, t('changePassword.validation.newPasswordRequired'));
        setupInput(confirmPasswordRef, t('changePassword.validation.confirmPasswordRequired'));
    };

    useEffect(() => {
        setupCustomValidation();
    }, [t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Vérifications personnalisées
        if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
            setError(t('changePassword.validation.allFieldsRequired'));
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError(t('changePassword.validation.passwordsMismatch'));
            return;
        }

        if (!validatePassword(formData.newPassword)) {
            setError(t('changePassword.validation.passwordComplexity'));
            return;
        }

        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const garageId = user._id || '';

            await authService.changePassword(
                garageId,
                formData.currentPassword,
                formData.newPassword
            );

            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

            setSuccess(true);
            setTimeout(() => setSuccess(false), 5000);

        } catch (err: any) {
            console.error('Erreur lors du changement de mot de passe:', err);
            setError(t('changePassword.validation.incorrectCurrentPassword'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`bg-white rounded-xl shadow-sm p-6 space-y-6 ${isRTL ? 'rtl' : ''}`}>
            <h3 className="text-lg font-medium text-gray-900 border-b pb-3 flex items-center">
                <Lock className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('changePassword.title')}
            </h3>

            {success && (
                <div className="rounded-lg bg-green-50 p-4 text-sm flex items-center border border-green-200">
                    <Check className={`h-5 w-5 text-green-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    <span className="text-green-700">{t('changePassword.success')}</span>
                </div>
            )}

            {error && (
                <div className="rounded-lg bg-red-50 p-4 text-sm flex items-center border border-red-200">
                    <AlertTriangle className={`h-5 w-5 text-red-400 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    <span className="text-red-700">{error}</span>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Mot de passe actuel */}
                <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('changePassword.form.currentPassword')}
                    </label>
                    <div className="relative">
                        <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            ref={currentPasswordRef}
                            id="currentPassword"
                            name="currentPassword"
                            type={showCurrentPassword ? "text" : "password"}
                            value={formData.currentPassword}
                            onChange={handleChange}
                            required
                            className={`block w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                            placeholder={t('changePassword.form.currentPasswordPlaceholder')}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <button
                            type="button"
                            className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                            {showCurrentPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Nouveau mot de passe */}
                <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('changePassword.form.newPassword')}
                    </label>
                    <div className="relative">
                        <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            ref={newPasswordRef}
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            value={formData.newPassword}
                            onChange={handleChange}
                            required
                            className={`block w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                            placeholder={t('changePassword.form.newPasswordPlaceholder')}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <button
                            type="button"
                            className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                            onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                            {showNewPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Confirmer le nouveau mot de passe */}
                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('changePassword.form.confirmPassword')}
                    </label>
                    <div className="relative">
                        <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                            <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            ref={confirmPasswordRef}
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            className={`block w-full ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isRTL ? 'text-right' : 'text-left'}`}
                            placeholder={t('changePassword.form.confirmPasswordPlaceholder')}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <button
                            type="button"
                            className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                            {showConfirmPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Exigences de mot de passe */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {t('changePassword.requirements.title')}
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                        <li className="flex items-center">
                            <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                            {t('changePassword.requirements.minLength')}
                        </li>
                        <li className="flex items-center">
                            <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                            {t('changePassword.requirements.uppercase')}
                        </li>
                        <li className="flex items-center">
                            <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                            {t('changePassword.requirements.digit')}
                        </li>
                        <li className="flex items-center">
                            <div className={`h-1.5 w-1.5 bg-gray-400 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                            {t('changePassword.requirements.specialChar')}
                        </li>
                    </ul>
                </div>

                <div>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                            loading ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                    >
                        {loading ? (
                            <>
                                <svg className={`animate-spin ${isRTL ? 'mr-1 ml-3' : '-ml-1 mr-3'} h-5 w-5 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('changePassword.form.updating')}
                            </>
                        ) : (
                            t('changePassword.form.submit')
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};